import { Err, isOk, Result } from "@pacote/result"
import useSWR from "swr"
import useSWRMutation from "swr/mutation"
import { BaseApiErrorResponse, BaseApiResponse } from "./api-config"
import { baseClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type ChangePasswordPayload = {
  jwt: string
  token: string
  password: string
  billId: number
}

type ChangePasswordServiceResponse = BaseApiResponse

type TchangePasswordErrorResponse = {
  type: "CODE_ERROR" | "UNKNOWN"
  errorMessage?: string
}

type ChangePasswordFetcherReturn = Result<
  ChangePasswordServiceResponse,
  TchangePasswordErrorResponse
>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

const changePasswordServiceEndpoint = "/gestisci-password-ms/ms/password/change"

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/

const changePasswordFetcher = async (
  key: string,
  { arg }: { arg: ChangePasswordPayload },
): Promise<ChangePasswordFetcherReturn> => {
  const responseResult = await baseClientFetcher<
    ChangePasswordServiceResponse,
    BaseApiErrorResponse
  >({
    url: key,
    method: "POST",
    body: arg,
  })

  if (isOk(responseResult)) {
    return responseResult
  }

  const response = responseResult.value

  if (response.status !== 422)
    return Err({
      type: "UNKNOWN",
    })

  const errorInfos = response.data.error?.errorInfos

  const nameError =
    errorInfos && (Object.keys(errorInfos || {})[0] as keyof typeof errorInfos)

  if (!errorInfos || !nameError || !errorInfos[nameError]?.length)
    return Err({
      type: "UNKNOWN",
    })

  const errorMessage = errorInfos[nameError][0]

  return Err({
    type: "CODE_ERROR",
    errorMessage: errorMessage,
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useChangePasswordMutation = () => {
  const { data } = useSWR<ChangePasswordFetcherReturn>(
    changePasswordServiceEndpoint,
  )

  const { trigger, ...rest } = useSWRMutation(
    changePasswordServiceEndpoint,
    changePasswordFetcher,
    { populateCache: true },
  )

  return {
    ...rest,
    data,
    changePassword: trigger,
  }
}

export { useChangePasswordMutation }
